import React, {useEffect, useRef, useState} from 'react';
import {HINT_UPDATE} from './hintKinds';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../../FaIcon';
import {
    FACT_TYPE_MODIFIER, ORIGIN, factTypeLabelKey, originLabelKey,
} from '../../memory/factConstants';
import {FactTypeHelp} from '../../memory/FactTypeHelp';
import {docSourceLabelKey} from '../../memory/docConstants';
import {sourceDate} from '../../memory/factDates';
import {stampOpacity, swipeTransform, useSwipeCard} from './useSwipeCard';

/**
 * Una carta del mazo: un hecho pendiente, con todo lo que hace falta para decidirlo sin salir de
 * la carta —qué dice, de qué tipo es, quién lo afirmó, de dónde salió y con qué palabras textuales—.
 *
 * **Todo lo que se toca es un `<button>` de verdad.** El mockup resuelve las ~25 acciones de esta
 * pantalla con `<span onclick>`: no reciben foco, no responden a Enter ni a Espacio, no se anuncian
 * como accionables y un usuario de teclado no puede ni editar ni fusionar. Es la diferencia entre
 * "se ve igual" y "funciona igual".
 *
 * La carta NO decide nada: avisa hacia arriba (aprobar / rechazar / editar / fusionar) y el mazo
 * orquesta la salida y llama al store. Así el gesto, el teclado y los botones de la arena terminan
 * los tres en el mismo camino.
 */

// Tope del texto de un hecho — el mismo que usa `FactEditModal` contra el backend.
const MAX_STATEMENT_LENGTH = 2000;

/**
 * El editor de un hecho, compartido por la carta (edición inline) y por el modo "Lista completa".
 * Es UN editor: si la lista tuviera el suyo, guardar desde un lado y desde el otro serían dos
 * comportamientos distintos para la misma acción.
 */
export function ReviewEditor({fact, busy, onSave, onCancel, variant}) {
    const {t} = useTranslation();
    const [text, setText] = useState(fact.statement || '');
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current && ref.current.focus) ref.current.focus();
    }, []);

    const trimmed = text.trim();
    const save = () => {
        if (!trimmed || busy) return;
        onSave(fact, trimmed);
    };

    // Escape y Ctrl/⌘+Enter se atajan ACÁ y se frena la propagación: el mazo escucha el teclado en
    // `document` y sin esto un Escape mientras se edita llegaría también a la pantalla de atrás.
    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            e.stopPropagation();
            save();
        }
    };

    return (
        <div className={`iabr-edit ${variant === 'row' ? '-row' : ''}`}>
            <textarea
                ref={ref}
                className="iabr-editbox"
                rows={3}
                value={text}
                maxLength={MAX_STATEMENT_LENGTH}
                aria-label={t('ia_booster.deck.edit_label')}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
            />
            <div className="iabr-edit-acts">
                <button type="button" className="iabr-btn -primary"
                        disabled={!trimmed || busy} onClick={save}>
                    {t('ia_booster.deck.save_and_approve')}
                </button>
                <button type="button" className="iabr-btn -ghost" disabled={busy} onClick={onCancel}>
                    {t('ia_booster.deck.cancel')}
                </button>
                <span className="iabr-edit-hint">{t('ia_booster.deck.edit_hint')}</span>
            </div>
        </div>
    );
}

export function ReviewCard({
    fact, pos, documentsById, similar, similarKind, busy, interactive, exiting, editing,
    onSwipe, onDragMove, onEdit, onCancelEdit, onSaveEdit, onMerge, onSplit,
    // «Abrir» sobre la fuente cuando el documento no tiene link externo: abre su crudo acá mismo.
    onOpenSource,
}) {
    const {t} = useTranslation();
    const {dragging, dx, dy, handlers} = useSwipeCard({
        enabled: !!interactive,
        onSwipe,
        onDragMove,
    });

    const doc = fact.source_document_id ? (documentsById || {})[fact.source_document_id] : null;
    const accent = FACT_TYPE_MODIFIER[fact.fact_type] || '-other';
    const docDate = doc ? sourceDate(doc) : null;
    const isAi = fact.origin === ORIGIN.AI_INFERRED;
    const hasProvenance = !!doc || !!fact.source_quote;
    // ¿Se puede abrir el crudo acá mismo? Hace falta quien lo reciba (el visor de la página) y que
    // el material haya dejado texto (`has_raw`; una imagen escaneada no). Con cita, el visor la
    // resalta y lleva hasta ahí: por eso el verbo es «Ver la cita» y no «Abrir».
    const canOpenRaw = !!doc && !!onOpenSource && doc.has_raw !== false;
    const hasQuote = !!fact.source_quote;
    // «Cambió»: el backend señaló que este hecho actualiza a otro (mismo sujeto, otro valor). Se
    // resuelve igual que un duplicado —fusionar—, pero la carta tiene que decir POR QUÉ: aprobar
    // «100 unidades» encima de «80 unidades» no es limpiar un repetido, es reemplazar un dato.
    const isUpdate = similarKind === HINT_UPDATE;
    // Fuentes múltiples: el hecho apareció en más de un documento (`sources`, cronológico). El
    // chip sigue nombrando la fuente ORIGINAL y suma «+N» con los otros títulos en el tooltip.
    const otherSources = Array.isArray(fact.sources)
        ? fact.sources
            .map(s => s && s.document_id)
            .filter((id, i, all) => id && id !== fact.source_document_id && all.indexOf(id) === i)
        : [];
    const otherTitles = otherSources
        .map(id => ((documentsById || {})[id] || {}).title)
        .filter(Boolean)
        .join(' · ');

    // El tinte del borde arranca antes que el umbral: es el aviso de "por acá vas", no el de "ya
    // está decidido" (ese es el sello, que llega a opacidad plena justo en el umbral).
    const classes = ['iabr-card', accent];
    if (dragging) classes.push('-dragging');
    if (dx > 18) classes.push('-tilt-r');
    if (dx < -18) classes.push('-tilt-l');
    if (exiting) classes.push(exiting === 'right' ? '-fly-r' : '-fly-l');
    if (editing) classes.push('-editing');

    const style = dragging ? {transform: swipeTransform(dx, dy)} : undefined;

    return (
        <article className={classes.join(' ')} data-pos={pos} style={style}
                 aria-label={fact.statement} {...(interactive ? handlers : {})}>
            {/* Los sellos son decorativos: lo que dicen ya lo dicen los nubs de la arena, que son
                botones de verdad. `aria-hidden` para no leerlos dos veces. */}
            <span className="iabr-stamp -ok" aria-hidden="true"
                  style={{opacity: stampOpacity(dx, 'right')}}>
                {t('ia_booster.deck.stamp_approve')}
            </span>
            <span className="iabr-stamp -no" aria-hidden="true"
                  style={{opacity: stampOpacity(dx, 'left')}}>
                {t('ia_booster.deck.stamp_reject')}
            </span>
            <span className={`iabr-edge ${accent}`} aria-hidden="true"/>

            <div className="iabr-chips">
                {/* La definición del tipo va al pasar el mouse por el rótulo, no como línea fija
                    debajo (decisión de fede, 08/09). Es el MISMO componente y el mismo texto que
                    la cola clásica: si cada superficie escribiera el suyo, el mismo tipo diría
                    cosas distintas en cada pantalla. `-below-left` porque la carta es
                    `overflow: hidden` y el cartel por default se abre donde no hay lugar. */}
                <FactTypeHelp type={fact.fact_type} placement="-below-left"
                              className={`iabr-chip -type ${accent}`}>
                    {t(factTypeLabelKey(fact.fact_type))}
                </FactTypeHelp>
                <span className={`iabr-chip ${isAi ? '-ai' : '-client'}`}>
                    {t(originLabelKey(fact.origin))}
                </span>
                {!editing && (
                    <button type="button" className="iabr-chip -edit" disabled={busy}
                            onClick={() => onEdit(fact)}>
                        {t('ia_booster.deck.edit')}
                    </button>
                )}
            </div>

            {editing ? (
                <ReviewEditor fact={fact} busy={busy} onSave={onSaveEdit} onCancel={onCancelEdit}/>
            ) : (
                <p className="iabr-statement">{fact.statement}</p>
            )}

            {/* El duplicado se resuelve DENTRO de la carta: mandarlo a otra pantalla convierte la
                decisión más fácil del mazo (ya la tomaste una vez) en la más cara. */}
            {similar && !editing && (
                <div className="iabr-dup">
                    <p className="iabr-dup-text">
                        {/* `copy` y no `code-branch`: los íconos se registran a mano en
                            `init_icons_library.js` y el que no está NO renderiza (sin error). */}
                        <FaIcon iconName="copy"/>
                        <span className="iabr-dup-label">
                            {t(isUpdate ? 'ia_booster.deck.update_of' : 'ia_booster.deck.duplicate')}
                        </span>
                        {/* El texto del hecho ya aprobado va como children de React, nunca
                            interpolado en una clave: lo escribe la extracción, no nosotros. */}
                        <q className="iabr-dup-quote">{similar.statement}</q>
                        {isUpdate && (
                            <span className="iabr-dup-hint">{t('ia_booster.deck.update_of_hint')}</span>
                        )}
                    </p>
                    <div className="iabr-dup-acts">
                        <button type="button" className="iabr-btn -primary" disabled={busy}
                                onClick={() => onMerge(fact, similar)}>
                            {t('ia_booster.deck.merge_approve')}
                        </button>
                        <button type="button" className="iabr-btn -ghost" disabled={busy}
                                onClick={() => onSplit(fact)}>
                            {t('ia_booster.deck.split')}
                        </button>
                    </div>
                </div>
            )}

            {hasProvenance && (
                <div className="iabr-src">
                    <div className="iabr-src-row">
                        <span className="iabr-src-ic" aria-hidden="true">
                            <FaIcon iconName="file-lines"/>
                        </span>
                        <span className="iabr-src-g" title={otherTitles || undefined}>
                            <span className="iabr-src-n">
                                {doc ? doc.title : t('ia_booster.deck.source_unknown')}
                            </span>
                            <span className="iabr-src-m">
                                {[doc ? t(docSourceLabelKey(doc.source)) : null, docDate,
                                    otherSources.length
                                        ? t('ia_booster.deck.sources_more', {count: otherSources.length})
                                        : null]
                                    .filter(Boolean).join(' · ')}
                            </span>
                        </span>
                        {/* La acción principal es el crudo ACÁ, posicionado en la cita: es lo
                            que responde «¿de dónde salió esto?» en un click, venga el material de
                            Drive, de una URL o de un texto pegado. En el mockup «Abrir →» está en
                            TODAS las cartas; hasta el 2026-09-12 las de Drive mandaban al original
                            en otra pestaña, donde la cita había que buscarla a mano. */}
                        {canOpenRaw && (
                            <button type="button" className="iabr-src-open"
                                    aria-label={hasQuote
                                        ? `${t('ia_booster.deck.source_quote_in')}: ${doc.title}`
                                        : undefined}
                                    onClick={() => onOpenSource(doc, fact)}>
                                {t(hasQuote ? 'ia_booster.deck.source_quote' : 'ia_booster.deck.source_open')}
                                <FaIcon iconName="file-lines"/>
                            </button>
                        )}
                        {/* El original (Drive, la página web) queda a un click: como acción con
                            verbo cuando es la única salida, como ícono secundario cuando el crudo
                            ya se abre acá. */}
                        {doc && doc.web_view_link && (
                            <a className={`iabr-src-open ${canOpenRaw ? '-ext' : ''}`}
                               href={doc.web_view_link}
                               target="_blank" rel="noopener noreferrer"
                               aria-label={canOpenRaw ? t('ia_booster.deck.source_original') : undefined}
                               title={canOpenRaw ? t('ia_booster.deck.source_original') : undefined}>
                                {!canOpenRaw && t('ia_booster.deck.source_open')}
                                <FaIcon iconName="up-right-from-square"/>
                            </a>
                        )}
                    </div>
                    {fact.source_quote && (
                        <blockquote className="iabr-src-q">{`“${fact.source_quote}”`}</blockquote>
                    )}
                </div>
            )}
        </article>
    );
}

export default ReviewCard;
